import { Link } from "react-router";
import { ArrowRight, Plus } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  Subtitle,
  VButton,
  VKpiCard,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useRequestsByRequester } from "@/domains/requests";
import { RequestStatusBadge } from "@/domains/requests";

export function RequesterDashboard(): React.JSX.Element {
  const user = useCurrentUser();
  const { data: requests } = useRequestsByRequester(user.id);

  const list = requests ?? [];
  const open = list.filter((r) => r.status !== "completed" && r.status !== "rejected");
  const approved = list.filter((r) => r.status === "approved").length;
  const inReview = list.filter((r) => r.status === "inReview").length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welkom terug, ${user.firstName}`}
        subtitle="Overzicht van jouw camera-aanvragen en lopende dossiers."
        actions={
          <VButton asChild>
            <Link to="/requests/new">
              <Plus className="size-4" aria-hidden />
              Nieuwe aanvraag
            </Link>
          </VButton>
        }
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <VKpiCard label="Mijn aanvragen" value={list.length} helper="Totaal ingediend" />
        <VKpiCard label="Goedgekeurd" value={approved} helper="Cameratoezicht loopt of staat klaar" />
        <VKpiCard label="In behandeling" value={inReview} helper="Wacht op feedback" />
      </div>

      <Card padding="lg" className="space-y-3">
        <div className="flex items-center justify-between">
          <CardTitle>Lopende aanvragen</CardTitle>
          <Link to="/requests" className="text-sm font-medium text-primary-700 hover:underline">
            Alle aanvragen →
          </Link>
        </div>
        {open.length === 0 ? (
          <EmptyState
            title="Geen lopende aanvragen"
            description="Maak een nieuwe aanvraag om mobiel cameratoezicht te starten."
            action={
              <VButton asChild>
                <Link to="/requests/new">Nieuwe aanvraag</Link>
              </VButton>
            }
          />
        ) : (
          <ul className="space-y-2">
            {open.map((req) => (
              <li key={req.id}>
                <Link
                  to={`/requests/${req.id}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-3 transition-colors hover:bg-muted/40"
                >
                  <div className="min-w-0">
                    <Body weight="semibold">{req.title}</Body>
                    <Caption tone="muted">
                      {req.number} · {req.period.startDate} → {req.period.endDate}
                    </Caption>
                  </div>
                  <div className="flex items-center gap-2">
                    <RequestStatusBadge status={req.status} />
                    <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card padding="lg">
        <Subtitle>
          Tip: voeg foto's van het plaatsbezoek toe aan je aanvraag. Hoe duidelijker de
          kijkrichting is gedocumenteerd, hoe sneller de plaatsing kan starten.
        </Subtitle>
      </Card>
    </div>
  );
}
